import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import db from "../../firebase/firebase"; // your initialized firestore export (default)
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { useLocation, useNavigate } from "react-router-dom";

export default function AddLecture() {
  // UI state
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Router
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode
  const editModel = location.state?.model || null;
  const isEditMode = !!editModel;

  // Lecture fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // File states
  const [existingFileUrl, setExistingFileUrl] = useState(editModel?.fileUrl || "");
  const [existingFileName, setExistingFileName] = useState(editModel?.fileName || "");
  const [existingFilePath, setExistingFilePath] = useState(editModel?.filePath || ""); // used to delete old file if replaced

  // Selected new file (not yet uploaded)
  const [selectedFile, setSelectedFile] = useState(null);

  // Ensure form prefilled when arriving in edit mode (useEffect to handle async navigation)
  useEffect(() => {
    if (isEditMode) {
      setTitle(editModel.title || "");
      setDescription(editModel.description || "");
      setExistingFileUrl(editModel.fileUrl || "");
      setExistingFileName(editModel.fileName || "");
      setExistingFilePath(editModel.filePath || "");
      setSelectedFile(null);
    }
  }, [isEditMode, editModel]);

  // Storage helper (assumes firebase app was initialized elsewhere)
  const storage = getStorage();

  // Upload a file to firebase storage, returns { url, path, name }
  const uploadFileToStorage = (file) => {
    return new Promise((resolve, reject) => {
      try {
        const timestamp = Date.now();
        const filePath = `lectures/${timestamp}_${file.name}`;
        const storageReference = storageRef(storage, filePath);
        const uploadTask = uploadBytesResumable(storageReference, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(prog);
          },
          (error) => {
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                path: filePath,
                name: file.name,
              });
            } catch (err) {
              reject(err);
            }
          }
        );
      } catch (err) {
        reject(err);
      }
    });
  };

  // Remove selected file before upload (clears the chosen file)
  const clearSelectedFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  // Handler for file input
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file only.");
      e.target.value = null;
      return;
    }
    const maxMB = 10;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File too large. Maximum allowed size is ${maxMB} MB.`);
      e.target.value = null;
      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);
  };

  // Main submit function
  const submitLecture = async () => {
    if (!title?.trim()) {
      alert("Title is required");
      return;
    }

    setLoading(true);

    try {
      let fileData = null;

      // If user selected a new file, upload it
      if (selectedFile) {
        fileData = await uploadFileToStorage(selectedFile);
      }

      // If editing & user uploaded new file -> delete previous file from storage (if path exists)
      if (isEditMode && selectedFile && existingFilePath) {
        try {
          const oldRef = storageRef(storage, existingFilePath);
          await deleteObject(oldRef).catch((err) => {
            // ignore "object-not-found" errors, but log others
            if (err.code !== "storage/object-not-found") {
              console.warn("Failed to delete old file:", err);
            }
          });
        } catch (err) {
          console.warn("deleteObject error (ignored):", err);
        }
      }

      // Prepare payload for Firestore
      const payload = {
        title: title.trim(),
        description: description.trim(),
        updatedAt: serverTimestamp(),
      };

      // If we uploaded a file, add file fields to payload and update local states
      if (fileData) {
        payload.fileUrl = fileData.url;
        payload.fileName = fileData.name;
        payload.filePath = fileData.path;

        // update local existing file states so the UI shows the newly uploaded file immediately if needed
        setExistingFileUrl(fileData.url);
        setExistingFileName(fileData.name);
        setExistingFilePath(fileData.path);
      }

      if (isEditMode) {
        // EDIT
        await updateDoc(doc(db, "lectures", editModel.id), payload);
      } else {
        // ADD NEW
        await addDoc(collection(db, "lectures"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      // On success, navigate back to lectures listing
      navigate("/admin/lectures");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to save lecture. See console for details.");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div style={styles.container}>
      <h2>{isEditMode ? "Edit Chapter" : "Add New Chapter"}</h2>

      {/* Title */}
      <input
        style={styles.input}
        placeholder="Chapter Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {/* Description */}
      {/* <textarea
        style={styles.textarea}
        placeholder="Lecture Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      /> */}

      {/* PDF Upload / Existing File */}
      <div style={{ marginBottom: 12, marginTop:10 }}>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
          Chapter PDF
        </label>

        {existingFileUrl && !selectedFile && (
          <div style={styles.fileRow}>
            <div>
              <div style={{ fontSize: 14 }}>{existingFileName}</div>
              <a href={existingFileUrl} target="_blank" rel="noopener noreferrer">
                Open current PDF
              </a>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                style={styles.smallButton}
                onClick={() => {
                  // allow user to replace existing file: open file selector by clearing existing and focusing input
                  // We'll just let them choose a new file via the input below
                  alert("Use the Choose File button to select a new PDF to replace the existing one.");
                }}
              >
                Replace
              </button>
              <button
                style={{ ...styles.smallButton, backgroundColor: "#f44336", color: "white" }}
                onClick={async () => {
                  // Option: remove existing file reference from Firestore and delete storage object
                  if (!existingFilePath) {
                    // nothing to delete
                    setExistingFileUrl("");
                    setExistingFileName("");
                    return;
                  }
                  const confirmDel = window.confirm("Delete existing PDF from storage and remove it from this Chapter?");
                  if (!confirmDel) return;

                  setLoading(true);
                  try {
                    const oldRef = storageRef(storage, existingFilePath);
                    await deleteObject(oldRef).catch((err) => {
                      if (err.code !== "storage/object-not-found") {
                        console.warn("deleteObject error:", err);
                        throw err;
                      }
                    });

                    // Remove file fields from Firestore doc (if editing)
                    if (isEditMode) {
                      await updateDoc(doc(db, "lectures", editModel.id), {
                        fileUrl: null,
                        fileName: null,
                        filePath: null,
                        updatedAt: serverTimestamp(),
                      });
                    }

                    setExistingFileUrl("");
                    setExistingFileName("");
                    setExistingFilePath("");
                    alert("PDF removed.");
                  } catch (err) {
                    console.error(err);
                    alert("Failed to remove PDF. See console.");
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {/* File chooser */}
        <div style={{ marginTop: 8 }}>
          <input
            id="pdf-upload"
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            style={styles.fileInput}
          />
          {selectedFile && (
            <div style={{ marginTop: 8 }}>
              <div>
                Selected: <strong>{selectedFile.name}</strong>{" "}
                <button style={styles.inlineLinkButton} onClick={clearSelectedFile}>
                  Clear
                </button>
              </div>
              <div style={{ marginTop: 6 }}>
                Upload progress: {uploadProgress}%{" "}
                {uploadProgress > 0 && uploadProgress < 100 && <span> (will upload on Save)</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <button
        style={{
          marginTop: 0,
          fontSize: 16,
          backgroundColor: "green",
          color: "white",
          borderWidth: 0,
          padding: 10,
          borderRadius: 6,
          width: 300,
          height: 44,
          cursor: loading ? "not-allowed" : "pointer",
        }}
        onClick={submitLecture}
        disabled={loading}
      >
        {loading ? "Saving..." : isEditMode ? "Update Chapter" : "Save Chapter"}
      </button>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 820,
    margin: "40px auto",
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    fontFamily: "sans-serif",
  },
  input: {
    width: "100%",
    padding: 10,
    marginBottom: 10,
    fontSize: 16,
  },
  textarea: {
    width: "100%",
    padding: 10,
    marginBottom: 10,
    minHeight: 120,
    fontSize: 15,
  },
  row: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 10,
    border: "1px solid #eee",
    borderRadius: 6,
    backgroundColor: "#fafafa",
  },
  smallButton: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    backgroundColor: "#1976d2",
    color: "white",
    fontSize: 13,
  },
  inlineLinkButton: {
    marginLeft: 8,
    border: "none",
    background: "none",
    color: "#1976d2",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: 14,
  },
  fileInput: {
    fontSize: 14,
  },
};
